/**
 * CSRF double-submit cookie support.
 *
 * Names confirmed from the server:
 * - Cookie: `__csrf` (COOKIE_NAMES.CSRF in src/server/utils/cookies.ts),
 *   non-httpOnly so client JS can read it.
 * - Header: `X-CSRF-Token` (checked in src/server/middleware/csrf.ts).
 * - Enforced only on POST/PUT/PATCH/DELETE when a cookie-based access token
 *   is present; Bearer-authenticated and pre-session requests are exempt.
 */

const CSRF_COOKIE_NAME = "__csrf";
export const CSRF_HEADER_NAME = "X-CSRF-Token";

/** `code` on the server's CSRF 403 — src/server/middleware/csrf.ts. */
export const CSRF_ERROR_CODE = "CSRF_INVALID";

export const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let inMemoryToken: string | null = null;

/**
 * Take the token the auth routes echo in their response body. Where the API
 * host has no parent domain to scope cookies to (api.autar.ai — see
 * cookieDomainFromApiUrl in src/server/utils/cookies.ts), `__csrf` is host-only
 * to the API and unreadable from the app origin, so the body is the only way
 * the SPA can learn the value the browser is sending.
 */
export function captureCsrfToken(path: string, body: unknown): void {
  // Only the auth routes echo a token. Keying on the field alone would let any
  // response anywhere in the API overwrite it.
  if (!path.startsWith("/auth/")) return;
  if (!body || typeof body !== "object") return;
  const token = (body as { csrfToken?: unknown }).csrfToken;
  // Install only a usable value: a response that carries no token, or an empty
  // or non-string one, must not disarm a token that is still good.
  if (typeof token === "string" && token.length > 0) inMemoryToken = token;
}

export function clearCsrfToken(): void {
  inMemoryToken = null;
}

function csrfCookieValue(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function getCsrfToken(): string | null {
  // The cookie is what the browser sends and what the server compares against,
  // so wherever it is readable it is authoritative and stays current for every
  // tab. The captured value covers only the case where it is not readable.
  return csrfCookieValue() ?? inMemoryToken;
}
